const FIXTURE_PRODUCTS_PARAM = 'tf_styled_with';
const PERSONALIZED_PRODUCTS_PARAM = 'tf_styled_with_personalized';
const OPEN_PRODUCT_PARAM = 'tf_styled_with_open';
const MAX_PRODUCTS = 6;
const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

const searchParams = new URLSearchParams(window.location.search);
const isThemePreview = searchParams.has('preview_theme_id') || searchParams.has('_tf_generation');
const fixtureTemplate = document.querySelector('[data-gldn-styled-with-fixture-template]');

/** @param {string | null} value */
function parseHandles(value) {
  if (!value) return [];

  return [
    ...new Set(
      value
        .split(',')
        .map((handle) => handle.trim().toLowerCase())
        .filter((handle) => HANDLE_PATTERN.test(handle)),
    ),
  ].slice(0, MAX_PRODUCTS);
}

/** @param {string} handle */
function productPath(handle) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}products/${encodeURIComponent(handle)}`.replace(/\/\/{2,}/g, '/');
}

/** @param {string} handle */
function previewProductUrl(handle) {
  const url = new URL(productPath(handle), window.location.origin);

  for (const key of ['preview_theme_id', '_tf_generation']) {
    const value = searchParams.get(key);
    if (value) url.searchParams.set(key, value);
  }

  return url.toString();
}

/** @param {unknown} source */
function imageUrl(source) {
  if (typeof source !== 'string' || !source) return '';
  if (source.startsWith('//')) return `https:${source}`;
  return new URL(source, window.location.origin).toString();
}

/** @param {HTMLButtonElement} button */
function populateQuickShopButton(button) {
  const text = document.createElement('span');
  text.className = 'add-to-cart-text';

  const content = document.createElement('span');
  content.className = 'add-to-cart-text__content';
  const outer = document.createElement('span');
  const inner = document.createElement('span');
  inner.textContent = 'Quick Shop';

  outer.append(inner);
  content.append(outer);
  text.append(content);
  button.append(text);
}

/**
 * Build the same Swym-backed wishlist control used by server-rendered cards.
 * @param {Record<string, any>} product
 * @param {string} productUrl
 */
function buildWishlistProduct(product, productUrl) {
  const variant = product.variants?.[0] || {};
  const wishlist = document.createElement('wishlist-product');
  wishlist.dataset.productId = String(product.id || '');
  wishlist.dataset.variantId = String(variant.id || '');
  wishlist.dataset.variantTitle = String(variant.title || '');
  wishlist.dataset.productUrl = productUrl;
  wishlist.dataset.productTitle = String(product.title || '');
  wishlist.dataset.productPrice = String(Number(product.price || 0) / 100);
  wishlist.dataset.productImage = imageUrl(product.featured_image || product.images?.[0]);
  wishlist.dataset.productOptions = JSON.stringify(product.options || []);

  const trigger = document.createElement('div');
  trigger.className = 'wish-trigger';
  trigger.tabIndex = 0;
  trigger.style.display = 'none';
  trigger.innerHTML = `
    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden="true">
      <path d="M4.31802 7.28799C2.56066 9.04535 2.56066 11.8946 4.31802 13.652L12.0001 21.334L19.682 13.652C21.4393 11.8946 21.4393 9.04535 19.682 7.28799C17.9246 5.53063 15.0754 5.53063 13.318 7.28799L12.0001 8.60606L10.682 7.28799C8.92462 5.53063 6.07538 5.53063 4.31802 7.28799Z" stroke="#333333" stroke-linecap="square" stroke-linejoin="round"></path>
    </svg>
    <span class="visually-hidden">Add to Wishlist</span>
  `;
  wishlist.append(trigger);

  return wishlist;
}

/**
 * @param {Record<string, any>} product
 * @param {string} handle
 * @param {boolean} personalized
 * @param {string} sectionId
 */
function buildCard(product, handle, personalized, sectionId) {
  const item = document.createElement('div');
  item.className = 'resource-list__item';

  const card = document.createElement('product-card');
  card.className = 'gldn-styled-with-card';
  card.id = `gldn-styled-with-fixture-${sectionId}-${product.id}`;
  card.dataset.productId = String(product.id);
  card.dataset.productHandle = handle;
  card.dataset.gldnStyledWithCard = '';
  card.setAttribute(
    'style',
    '--quick-add-display: flex; --quick-add-mobile-display: flex; --quick-add-mobile-opacity: 1;',
  );
  if (personalized) card.dataset.gldnPersonalizedProduct = '';

  const productUrl = previewProductUrl(handle);
  const link = document.createElement('a');
  link.id = `${card.id}-link`;
  link.className = 'gldn-styled-with-card__product-link visually-hidden';
  link.href = productUrl;
  link.setAttribute('ref', 'productCardLink');
  link.tabIndex = -1;
  link.setAttribute('aria-hidden', 'true');
  link.textContent = product.title;

  const media = document.createElement('div');
  media.className = 'gldn-styled-with-card__media';

  const source = imageUrl(product.featured_image || product.images?.[0]);
  if (source) {
    const image = document.createElement('img');
    image.className = 'gldn-styled-with-card__image';
    image.src = source;
    image.alt = product.title;
    image.loading = 'lazy';
    image.sizes = '(min-width: 990px) 30vw, (min-width: 750px) 44vw, 78vw';
    media.append(image);
  }

  media.append(buildWishlistProduct(product, productUrl));

  const quickAdd = document.createElement('quick-add-component');
  quickAdd.className = 'quick-add quick-add--styled-with';
  quickAdd.setAttribute('ref', 'quickAdd');
  quickAdd.dataset.productTitle = product.title;
  quickAdd.dataset.quickAddButton = 'choose';
  quickAdd.dataset.productOptionsCount = String(product.options?.length || 0);
  quickAdd.dataset.styledWith = 'true';
  quickAdd.dataset.personalized = personalized ? 'true' : 'false';
  quickAdd.dataset.productUrl = productUrl;

  const button = document.createElement('button');
  button.className = 'button quick-add__button quick-add__button--choose add-to-cart-button';
  button.type = 'button';
  button.name = 'add';
  button.setAttribute('on:click', 'quick-add-component/handleClick');
  button.setAttribute('aria-label', `Quick Shop ${product.title}`);
  populateQuickShopButton(button);
  quickAdd.append(button);
  media.append(quickAdd);

  const title = document.createElement('h3');
  title.className = 'gldn-styled-with-card__title';
  title.textContent = product.title;

  card.append(link, media, title);
  item.append(card);
  return item;
}

/** @param {string} handle */
async function fetchProduct(handle) {
  try {
    const response = await fetch(`${productPath(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;

    const product = await response.json();
    return product?.available ? product : null;
  } catch (error) {
    console.warn(`[Styled With fixture] Could not load ${handle}.`, error);
    return null;
  }
}

async function hydrateFixture() {
  if (!isThemePreview || !(fixtureTemplate instanceof HTMLTemplateElement)) return;
  if (document.querySelector('[data-gldn-styled-with]:not([data-gldn-styled-with-fixture])')) return;

  const handles = parseHandles(searchParams.get(FIXTURE_PRODUCTS_PARAM));
  if (handles.length === 0) return;

  const personalizedHandles = new Set(parseHandles(searchParams.get(PERSONALIZED_PRODUCTS_PARAM)));
  const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);
  if (products.length === 0) return;

  const fragment = fixtureTemplate.content.cloneNode(true);
  if (!(fragment instanceof DocumentFragment)) return;

  const section = fragment.querySelector('[data-gldn-styled-with-fixture]');
  const slideshow = fragment.querySelector('slideshow-component');
  const slides = fragment.querySelector('slideshow-slides');
  if (!(section instanceof HTMLElement) || !(slideshow instanceof HTMLElement) || !(slides instanceof HTMLElement)) {
    return;
  }

  const sectionId = fixtureTemplate.dataset.sectionId || 'fixture';
  section.dataset.productCount = String(products.length);
  slideshow.style.setProperty(
    '--slideshow-timeline',
    products.map((_, index) => `--slide-${index}`).join(', '),
  );

  products.forEach((product, index) => {
    const handle = String(product.handle || handles[index]);
    const slide = document.createElement('slideshow-slide');
    slide.className = 'resource-list__slide';
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
    slide.append(buildCard(product, handle, personalizedHandles.has(handle), sectionId));
    slides.append(slide);
  });

  fixtureTemplate.before(fragment);

  const requestedOpen = searchParams.get(OPEN_PRODUCT_PARAM);
  if (!requestedOpen) return;

  const openHandle = requestedOpen === '1' ? String(products[0].handle || handles[0]) : requestedOpen.toLowerCase();
  await customElements.whenDefined('quick-add-component');
  requestAnimationFrame(() => {
    const openButton = document.querySelector(
      `[data-gldn-styled-with-fixture] [data-product-handle="${CSS.escape(openHandle)}"] .quick-add__button--choose`,
    );
    if (openButton instanceof HTMLButtonElement) openButton.click();
  });
}

hydrateFixture();
